/** MV6: the contest half of a walk. Geometry and admission belong to overrun-walk / plain-walk-scope. */
import { HandBack, Refusal, ecChanges, ecLean, itemField, record, scalar, upper, type JanusCtx, type JanusReaders, type Row } from '../ability/context.js';
import { selectSkillForContestingRoll } from '../ability/select-skill-for-contesting-roll.js';
import { skillRollArr, CONTEST_ROLL_ID } from '../ability/skill-roll.js';
import { adjustCharges } from '../ability/charges/adjust-charges.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';
import { resourceReductionReaders } from '../ability/resource-reduction.js';
import { fortunateOffersOf } from '../ability/trigger-consent.js';
import { payloadDryRunRollSource } from '../ability/environmental-payload.js';
import { recordingRollSource, type RollSource } from '../actions/roll-source.js';
import { type OverrunTrigger, OVERRUN_NO_ABILITY_MESSAGE, OVERRUN_NO_CHARGES_MESSAGE } from './overrun-walk.js';
import { decisionWrites, movementDecisionView, finishMovementDecision } from './decision-view.js';

export const FULL_OVERRUN = '1DC3B3DE-BD4C-B77A-429D-12C7F2B2439D';
export const OVERRUN_FORTUNATE = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
export const OVERRUN_ROW_TYPES = new Set([CONTEST_ROLL_ID, 'adjust_charges', 'unlucky_roll', 'gained_ascension_skill_point',
  'failed_roll_for_ascension_skill_point', 'ascension_skill_tier_up', 'no_roll', 'reaction_ability_applied', 'status_effect_applied', 'resource_reduction_prevented']);
/** MV8 (MV7 Codex r1 P2): out of combat, a (mover, target) pair this page recorded a contest for in the last few seconds is refused PRE-DRAW
 *  — the walk halts on the foot before that target with this message (the page owns the exploration window). */
export const OVERRUN_COOLDOWN_MESSAGE = 'You just tried to overrun that creature — wait a moment before trying again.';
export interface OverrunContestInput { encounter: Row; members: ReadonlyMap<string, Row>; actorId: string; triggers: readonly OverrunTrigger[]; inCombat: boolean; read: JanusReaders;
  /** Exploration only: true for a target this mover contested within the window (page memory of its own recorded contests). */
  cooled?: (targetId: string) => boolean }
export interface OverrunEvidence { triggers: OverrunTrigger[]; blocked_step: number | null; revs: Record<string, number>; verdict: {
  rows: JanusCtx['rows']; ec: Record<string, Record<string, unknown>>; ecBefore: Record<string, Record<string, unknown>>;
  charges: JanusCtx['chargesChanged']; statusesAdded: JanusCtx['statusesAdded']; pc: NonNullable<JanusCtx['playerCharactersChanged']>;
}; fortunateOffers?: string[] }
export type OverrunContestPlan = { kind: 'handback'; reason: string } | { kind: 'resolved'; blockedStep: number | null; warning: string | null; evidence: OverrunEvidence };
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const entryId = (e: unknown): string => upper(record(e)?.ability_id ?? itemField(record(record(e)?.item_arr), 'item_id') ?? itemField(record(e), 'item_id') ?? e);
function context(input: OverrunContestInput, roll: RollSource): JanusCtx {
  const consent = input.read.triggerConsent;
  // Match the attack lane: a controller the page cannot name keeps inline auto-fire.
  const read: JanusReaders = consent ? { ...input.read, triggerConsent: {
    controller: reactor => { const who = consent.controller(reactor); return who === '?' ? null : who; },
    answer: (reactorId, abilityId) => consent.answer(reactorId, abilityId),
    need: request => consent.need(request), pending: () => consent.pending(),
    get final() { return consent.final; },
  } } : input.read;
  return { ...movementDecisionView(input.encounter, input.members),
    read, roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: null, rollPrivate: 0 };
}
function run(ctx: JanusCtx, input: OverrunContestInput, preflight = false): { blockedStep: number | null; warning: string | null } {
  const mover = ctx.members.get(upper(input.actorId));
  const full = ctx.read.definition(FULL_OVERRUN);
  if (!mover || !full) throw new HandBack('overrun:definition-or-mover-not-held');
  const first = input.triggers[0]?.step;
  if (first === undefined) throw new HandBack('overrun:empty-triggers');
  const entry = ctx.read.entries(mover, 'free_ability').find(e => entryId(e) === FULL_OVERRUN);
  if (!entry) return { blockedStep: first, warning: OVERRUN_NO_ABILITY_MESSAGE };
  if (input.inCombat) {
    const raw = scalar(record(record(entry)?.charges)?.current ?? record(entry)?.ability_charges);
    const current = raw === 'NA' || raw === '' || raw == null ? 1 : Math.trunc(Number(raw));
    if (!Number.isSafeInteger(current)) throw new HandBack('overrun:charge-shape');
    // The same reader the spend below (adjustCharges → resourceReductionReaders) judges with: pending status edits included.
    if (current <= 0 && chargeReductionDelta(mover, -1, resourceReductionReaders(ctx)) < 0) return { blockedStep: first, warning: OVERRUN_NO_CHARGES_MESSAGE };
    // Normalize the held sentinel in this run's field view for the shared charge planner.
    const row = record(entry);
    if (!row) throw new HandBack('overrun:charge-entry-shape');
    row.charges = { ...(record(row.charges) ?? {}), current, max: Number(scalar(record(row.charges)?.max ?? row.max_ability_charges)) || 1 };
    if (adjustCharges(ctx, mover, FULL_OVERRUN, -1, true) === null)
      return { blockedStep: first, warning: 'Overrun could not be charged — movement halted.' };
  }
  const roll = (roller: Row, key: string, to: Row | null): number => {
    const skill = selectSkillForContestingRoll(ctx, roller, ctx.read.entries(full, key));
    const flag = scalar(ctx.read.value(roller, 'overrun_roll_adv'));
    if (flag != null && flag !== '' && flag !== false && flag !== 0 && flag !== '0') skill.adv += 1;
    return skillRollArr(ctx, { casterId: upper(input.actorId), targetIds: [], abilityId: FULL_OVERRUN, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: null, useAbilityVariable2: null }, roller, skill, to, 0, { isTarget: false, asTarget: false });
  };
  const caster = roll(mover, 'skill_roll_contester', null);
  let blockedStep: number | null = null;
  for (const trigger of input.triggers) for (const id of trigger.ids) {
    const target = ctx.members.get(upper(id));
    if (!target) throw new HandBack('overrun:target-not-held');
    if (caster < roll(target, 'skill_roll_contestee', mover)) {
      blockedStep ??= trigger.step;
      if (!preflight) return { blockedStep, warning: null };
    }
  }
  return { blockedStep, warning: null };
}
function commitShape(ctx: JanusCtx, input: OverrunContestInput): string | null {
  const parties = new Set([upper(input.actorId), ...input.triggers.flatMap(t => t.ids.map(upper))]);
  if (ctx.statusesRemoved.length || ctx.inventoryChanged.length || (ctx.statusStacks ?? []).length || (ctx.concentrationBreaks ?? []).length || (ctx.deathTail ?? []).length) return 'overrun:unsupported-writes';
  if (ctx.rows.some(r => !OVERRUN_ROW_TYPES.has(r.roll_type_1))) return 'overrun:row-type';
  for (const [id, cols] of Object.entries(ecChanges(ctx))) {
    if (!parties.has(upper(id)) || Object.keys(cols).some(c => c !== 'luck' && c !== 'reaction')) return 'overrun:column-outside-allowlist';
    if ('reaction' in cols && (Number(ecLean(input.members.get(upper(id))!, 'reaction')) !== 1 || cols.reaction !== 0
      || !ctx.statusesAdded.some(s => upper(s.target) === upper(id) && upper(s.status_item_id) === OVERRUN_FORTUNATE)))
      return 'overrun:column-outside-allowlist';
  }
  if (ctx.chargesChanged.length > 1 || ctx.chargesChanged.some(c => !input.inCombat || upper(c.holder) !== upper(input.actorId) || upper(c.item_id) !== FULL_OVERRUN || c.charges !== c.before - 1)) return 'overrun:charge-outside-allowlist';
  if (ctx.statusesAdded.some(s => !parties.has(upper(s.target)) || upper(s.status_item_id) !== OVERRUN_FORTUNATE)) return 'overrun:status-outside-allowlist';
  const pcIds = new Set([...parties].map(id => upper(ecLean(ctx.members.get(id)!, 'item_id') ?? itemField(ctx.members.get(id) ?? null, 'item_id'))));
  if ((ctx.playerCharactersChanged ?? []).some(p => !pcIds.has(upper(p.item_id)))) return 'overrun:pc-outside-allowlist';
  return null;
}
// Codex MV6 r3 P2: every TARGET's movement_rev as the page planned against it — the recorder's CAS (a target that moved since is stale;
// the mover's own row is the walk's expected_rev CAS). MV7 (coordinator Round 29 P3): read + validated BEFORE any die, live or dry.
// (A missing rev reads as 0: the compiled row elides a zero column — MV7 r1 P3, intentional.)
function partyRevs(input: OverrunContestInput, triggers: readonly OverrunTrigger[]): Record<string, number> | null {
  const revs: Record<string, number> = {};
  for (const id of triggers.flatMap(t => t.ids.map(upper))) {
    const member = input.members.get(id);
    const rev = member ? Number(ecLean(member, 'movement_rev') ?? 0) : NaN;
    if (!Number.isSafeInteger(rev) || rev < 0) return null;
    revs[id] = rev;
  }
  return revs;
}
/** Four extreme-dice preflights isolate their writes before the live source is touched. */
export function resolveOverrunContest(input: OverrunContestInput, roll: RollSource): OverrunContestPlan {
  // MV8: the exploration pair window, pre-draw. A cooled target in the FIRST trigger halts the walk there with no roll (the recorder's
  // "empty contest" shape, as a missing ability does); a later one contests only the triggers before it and halts on the foot before it.
  const cut = input.inCombat || !input.cooled ? -1 : input.triggers.findIndex(t => t.ids.some(id => input.cooled!(upper(id))));
  if (cut === 0) {
    const first = input.triggers[0]!, revs = partyRevs(input, [first]);
    if (!revs) return { kind: 'handback', reason: 'overrun:party-rev' };
    return { kind: 'resolved', blockedStep: first.step, warning: OVERRUN_COOLDOWN_MESSAGE, evidence: { triggers: clone([first]), blocked_step: first.step, revs,
      verdict: { rows: [], ec: {}, ecBefore: {}, charges: [], statusesAdded: [], pc: [] } } };
  }
  if (cut > 0) {
    const prefix = resolveOverrunContest({ ...input, triggers: input.triggers.slice(0, cut), cooled: undefined }, roll);
    return prefix.kind === 'resolved' && prefix.blockedStep === null
      ? { ...prefix, blockedStep: input.triggers[cut]!.step, warning: prefix.warning ?? OVERRUN_COOLDOWN_MESSAGE } : prefix;
  }
  try {
    const revs = partyRevs(input, input.triggers);
    if (!revs) return { kind: 'handback', reason: 'overrun:party-rev' };
    for (const pattern of ['max', 'min', 'alt-hi', 'alt-lo'] as const) {
      const dry = context(input, payloadDryRunRollSource(pattern));
      dry.dryRun = true;
      // Every prospective roller is checked even if these synthetic faces lose an earlier contest.
      run(dry, input, true);
      const why = commitShape(dry, input);
      if (why) return { kind: 'handback', reason: `preflight:${pattern}:${why}` };
    }
    const ctx = context(input, recordingRollSource(roll));
    const result = run(ctx, input);
    const why = commitShape(ctx, input);
    if (why) return { kind: 'handback', reason: why };
    const ec: OverrunEvidence['verdict']['ec'] = {}, ecBefore: OverrunEvidence['verdict']['ecBefore'] = {};
    for (const [id, cols] of Object.entries(ecChanges(ctx))) for (const [col, value] of Object.entries(cols)) {
      const before = ecLean(input.members.get(upper(id))!, col) ?? 0;
      if (String(before ?? '') === String(value ?? '')) continue;
      (ec[upper(id)] ??= {})[col] = value; (ecBefore[upper(id)] ??= {})[col] = before;
    }
    finishMovementDecision(ctx);
    return decisionWrites({ kind: 'resolved', ...result, evidence: { triggers: clone([...input.triggers]), blocked_step: result.blockedStep, revs,
      verdict: { rows: ctx.rows, ec, ecBefore, charges: ctx.chargesChanged, statusesAdded: ctx.statusesAdded.map(s => ({ ...s, status_id: s.status_id ?? ctx.read.uuid() })), pc: ctx.playerCharactersChanged ?? [] },
      ...fortunateOffersOf(ctx) } });
  } catch (error) {
    return { kind: 'handback', reason: error instanceof HandBack ? error.reason : error instanceof Refusal ? error.refusal : `overrun:exception:${String(error).slice(0, 120)}` };
  }
}
